import { useState, useCallback, createContext, useContext } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import Login from "@/components/Login";
import Dashboard from "@/components/Dashboard";
import PublicGenerator from "@/components/PublicGenerator";
import SupporterGuide from "@/components/SupporterGuide";
import JoinPage from "@/components/JoinPage";
import OutreachKit from "@/components/OutreachKit";
import CommunityStats from "@/components/CommunityStats";
import VolunteerConsent from "@/components/VolunteerConsent";
import VolunteerDashboard from "@/components/VolunteerDashboard";

const AuthContext = createContext(null);

export function useAuth() {
  return useContext(AuthContext);
}

function AuthProvider({ children }) {
  const [token, setToken] = useState(() => localStorage.getItem("cf_token"));
  const [user, setUser] = useState(() => localStorage.getItem("cf_user"));
  const [role, setRole] = useState(() => localStorage.getItem("cf_role") || "admin");

  const login = useCallback((tok, username, userRole = "admin") => {
    localStorage.setItem("cf_token", tok);
    localStorage.setItem("cf_user", username);
    localStorage.setItem("cf_role", userRole);
    setToken(tok);
    setUser(username);
    setRole(userRole);
  }, []);

  const logout = useCallback(() => {
    localStorage.removeItem("cf_token");
    localStorage.removeItem("cf_user");
    localStorage.removeItem("cf_role");
    setToken(null);
    setUser(null);
    setRole(null);
  }, []);

  return (
    <AuthContext.Provider value={{ token, user, role, login, logout, isAuth: !!token }}>
      {children}
    </AuthContext.Provider>
  );
}

function ProtectedRoute({ children, allowedRoles = ["admin", "volunteer"] }) {
  const { isAuth, role } = useAuth();
  if (!isAuth) return <Navigate to="/admin" replace />;
  if (!allowedRoles.includes(role)) return <Navigate to="/admin" replace />;
  return children;
}

function RoleBasedDashboard() {
  const { role } = useAuth();
  // Volunteers go to their own dashboard, admins to full dashboard
  return role === "volunteer" ? <VolunteerDashboard /> : <Dashboard />;
}

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <Routes>
          <Route path="/" element={<PublicGenerator />} />
          <Route path="/guide" element={<SupporterGuide />} />
          <Route path="/join" element={<JoinPage />} />
          <Route path="/outreach" element={<OutreachKit />} />
          <Route path="/community" element={<CommunityStats />} />
          <Route path="/volunteer" element={<VolunteerConsent />} />
          <Route path="/admin" element={<Login />} />
          <Route
            path="/admin/dashboard/*"
            element={
              <ProtectedRoute>
                <RoleBasedDashboard />
              </ProtectedRoute>
            }
          />
          <Route
            path="/volunteer/dashboard"
            element={
              <ProtectedRoute allowedRoles={["admin", "volunteer"]}>
                <VolunteerDashboard />
              </ProtectedRoute>
            }
          />
          <Route path="/dashboard/*" element={<Navigate to="/admin/dashboard" replace />} />
          <Route path="/login" element={<Navigate to="/admin" replace />} />
        </Routes>
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;
